import React, { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { Whitelist } from "../../Interfaces/interfaces";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { WhitelistRule } from "../../../../Interfaces/interfaces";
import { apiService } from "../../../../api/api";
import EditWhitelistRuleDialog from "../Dialogs/editWhitelistRuleDialog";
import RuleConfirmationDialog from "../Dialogs/ruleConfirmationDialog";
import { getTimestamp } from "../Utils/whitelistUtils";
import { FULL_PAGE_TABLE_SCROLL_HEIGHT } from "../../../../config/tableLayout";

interface WhitelistTableProps {
  rows: Whitelist[];
  loading: boolean;
}

const WhitelistTable: React.FC<WhitelistTableProps> = ({ rows, loading }) => {
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [removeDialogVisible, setRemoveDialogVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<WhitelistRule | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);

  const toast = useRef<Toast>(null);

  const handleSave = async (rule: WhitelistRule) => {
    try {
      await apiService.youtubeScorecard.updateWhitelistRule(rule);
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Rule updated successfully.",
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update the rule. Please try again.",
        life: 3000,
      });
    }
  };
  const handleRemove = async (rule: WhitelistRule) => {
    try {
      await apiService.youtubeScorecard.removeWhitelistRule(rule);
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Rule removed successfully.",
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to remove the rule. Please try again.",
        life: 3000,
      });
    }
  };

  const editButton = (rowData: WhitelistRule) => {
    return (
      <Button
        icon="pi pi-pencil"
        rounded
        size="small"
        style={{
          fontSize: "10px",
          height: "30px",
          width: "30px",
        }}
        onClick={() => {
          setSelectedRowData(rowData); // Set the selected row data
          setEditDialogVisible(true); // Open the edit dialog
        }}
      />
    );
  };

  const removeButton = (rowData: WhitelistRule) => {
    return (
      <Button
        icon="pi pi-trash"
        severity="danger"
        rounded
        size="small"
        style={{
          fontSize: "10px",
          height: "30px",
          width: "30px",
        }}
        onClick={() => {
          setSelectedRowData(rowData);
          setRemoveDialogVisible(true);
        }}
      />
    );
  };

  const items = Array.from({ length: 5 }, (v, i) => i);
  const skeletonRows = items.map(() => ({}));
  const dateAddedSort = (event: any) => {
    return [...event.data].sort((a, b) => {
      const result = getTimestamp(a) - getTimestamp(b);
      return event.order * result;
    });
  };

  useEffect(() => {
    if (!rows) {
      setIsLoading(true);
    } else if (rows.length > 0) {
      setIsLoading(false);
    }
  }, [rows]);

  return (
    <>
      {isLoading === true ? (
        <DataTable
          value={skeletonRows}
          size="normal"
          paginator
          rows={50}
          paginatorTemplate={
            "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink  RowsPerPageDropdown"
          }
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries" // Changed the template
          paginatorLeft
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
          showGridlines
          cellSelection
          stripedRows
          removableSort
          scrollable
          scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
          sortField="dateAdded"
          sortOrder={-1}
          sortMode="single"
          selectionMode="single"
          tableStyle={{ width: "auto", fontSize: "0.8rem" }}
          className="custom-datatable-paginator" // Add the custom class here
          style={{
            marginBottom: "-4rem",
            marginRight: "-1rem",
            marginLeft: "-1rem",
          }}
        >
          <Column
            header="Property"
            field="property"
            body={() => <Skeleton />}
            style={{ width: "12%" }}
            sortable
          />
          <Column
            header="Condition"
            field="condition"
            body={() => <Skeleton />}
            style={{ width: "12%" }}
            sortable
          />
          <Column
            header="Value"
            field="value"
            body={() => <Skeleton />}
            style={{ width: "12%" }}
            sortable
          />
          <Column
            header="Audit"
            field="audit"
            body={() => <Skeleton />}
            style={{ width: "12%" }}
            sortable
          />
          <Column
            header="Fields"
            field="fields"
            body={() => <Skeleton />}
            style={{ width: "12%" }}
            sortable
          />
          <Column
            header="User"
            field="user"
            body={() => <Skeleton />}
            style={{ width: "12%" }}
            sortable
          />
          <Column
            header="Date Added"
            field="dateAdded"
            body={() => <Skeleton />}
            style={{ width: "12%" }}
            sortable
            sortFunction={dateAddedSort}
          />
          <Column
            header="Notes"
            field="notes"
            body={() => <Skeleton />}
            style={{ width: "12%" }}
            sortable
          />
          <Column
            header="loading..."
            field="notes"
            body={() => <Skeleton />}
            style={{ width: "32%" }}
            sortable
          />
        </DataTable>
      ) : (
        <DataTable
          value={rows.length > 0 ? rows : []}
          size="small"
          paginator
          rows={50}
          paginatorTemplate={
            "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink  RowsPerPageDropdown"
          }
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries" // Changed the template
          paginatorLeft
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
          showGridlines
          cellSelection
          stripedRows
          removableSort
          scrollable
          scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
          sortField="dateAdded"
          sortOrder={-1}
          sortMode="single"
          selectionMode="single"
          tableStyle={{ width: "auto", fontSize: "0.8rem" }}
          className="custom-datatable-paginator" // Add the custom class here
          style={{
            marginBottom: "-4rem",
            marginRight: "-1rem",
            marginLeft: "-1rem",
          }}
        >
          {rows.length === 0 && loading === false && (
            <tr>
              <td colSpan={Column.length} style={{ textAlign: "center" }}>
                No matching rows found.
              </td>
            </tr>
          )}

          <Column header="Property" field="property" sortable />
          <Column header="Condition" field="condition" sortable />
          <Column
            header="Value"
            field="value"
            body={(rowData) => rowData.value.split(" ").join(", ")}
            sortable
          />
          <Column header="Audit" field="audit" sortable />
          <Column
            header="Fields"
            field="fields"
            body={(rowData) => rowData.fields.join(", ")}
            sortable
          />
          <Column header="User" field="user" sortable/>
          <Column header="Date Added" field="dateAdded" sortable sortFunction={dateAddedSort}/>
          <Column header="Notes" field="notes" sortable/>
          <Column
            header=""
            field=""
            body={(rowData) => (
              <div className="flex gap-2 align-items-center">
                <div>{editButton(rowData)}</div>
                <div>{removeButton(rowData)}</div>
              </div>
            )}
          />
        </DataTable>
      )}

      <EditWhitelistRuleDialog
        rowData={selectedRowData}
        visible={editDialogVisible}
        onHide={() => {
          setEditDialogVisible(false);
          setSelectedRowData(null);
        }}
        onSave={handleSave}
      />
      <RuleConfirmationDialog
        Title={"Are you sure you want to remove this rule?"}
        visible={removeDialogVisible}
        onHide={() => {
          setRemoveDialogVisible(false);
          setSelectedRowData(null);
        }}
        onSave={handleRemove}
        whitelistRule={selectedRowData}
      />
    </>
  );
};

export default WhitelistTable;
